import { formatFileSize } from '@libs/utils';

export { formatFileSize };

/**
 * Compact "x ago" relative time for upload timestamps, localized via
 * `Intl.RelativeTimeFormat`. Falls back to an absolute date for anything older
 * than a few weeks so the label stays meaningful.
 */
export function formatUploadedAgo(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSec);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];

  // Older than ~3 weeks → absolute date is clearer than "last month".
  if (abs >= 60 * 60 * 24 * 21) {
    return new Date(iso).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  for (const [unit, secs] of units) {
    if (abs >= secs) return rtf.format(Math.round(diffSec / secs), unit);
  }
  return rtf.format(diffSec, 'second');
}

/**
 * Absolute upload date and time for the table's dedicated "Uploaded" column,
 * split so the date and time can render on separate lines. Empty strings for
 * an unparseable timestamp.
 */
export function formatUploadedDateTime(
  iso: string,
  locale: string,
): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: d.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
  };
}
